/**
 * Cross stitch alphabets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const crossStitchAlphabetsPack: ToilePack = {
    slug: 'cross-stitch-alphabets',
    category: 'craft',
    name: 'Cross stitch alphabets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'block', label: 'Block letters', subject: 'a cross-stitch chart of the full alphabet "ABCDEFGHIJKLMNOPQRSTUVWXYZ" in block capitals seven squares tall, set in rows on a counting grid with a heavier line every ten squares'},
        {key: 'script', label: 'Script', subject: 'a cross-stitch chart of a joined script alphabet in lowercase "abcdefghijklmnopqrstuvwxyz", each letter about nine squares tall with half-stitches on the curves'},
        {key: 'sampler', label: 'Sampler', subject: 'a cross-stitch chart of a small sampler alphabet in capitals with the numbers "0123456789" beneath, framed by a simple border of small hearts'},
    ],
    styles: [
        {key: 'symbols', label: 'Symbol grid', style: 'each cross shown as a black symbol square on a fine counting grid, a heavier line every ten squares', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'crosses', label: 'Drawn crosses', style: 'each stitch drawn as a small X on the grid, whole crosses only', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'pixels', label: 'Pixel blocks', style: 'each stitch a solid black square on the grid so each letter reads as a pixel font', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['kids font', 'with numbers', 'monogram'],
    directionHint: 'try: kids font',
    seo: {
        title: 'Cross stitch alphabets: block, script, sampler',
        metaDescription: 'Nine cross stitch alphabet charts: block capitals, joined script and a sampler alphabet with numbers, built from whole crosses.',
        h1: 'Cross stitch alphabets: block, script and sampler',
        tagline: 'Full alphabets on a counting grid, each letter built from whole crosses.',
        intro: 'Stitchers chart alphabets to put names, initials and dates on samplers, baby gifts and bookmarks. These nine charts give three alphabets on a cross-stitch grid: block capitals, a joined lowercase script, and a sampler with numbers. Each comes as a symbol grid, a grid of drawn crosses, or solid pixel blocks. Type a different word or style and the same grid carries it.',
        useCases: [
            'initials on a baby gift',
            'a name on a sampler',
            'a bookmark with dates',
            'a numbered sampler',
        ],
        faq: [
            {q: 'Are these full alphabets?', a: 'Yes. Block capitals give A to Z, script gives a to z, and the sampler gives capitals and the numbers zero to nine.'},
            {q: 'Can I stitch a name with these?', a: 'Yes. Each letter is a block on the grid, so you can pick out the letters your name needs.'},
            {q: 'Which style is easiest to count?', a: 'The symbol grid, with one symbol per square and a heavier line every ten squares.'},
        ],
    },
}
